/**
 * Project Conversation + Review Queue — Phase 1 · Sprint 2.
 * "The gravitational centre of the platform."
 *
 * Layout: 3-column on desktop (rail comes from AppShell · this screen
 * renders the middle Conversation column + the right Review Queue aside),
 * stacked + RQ-as-bottom-sheet on mobile (mobile parity day-one).
 *
 * Data plumbing: Firestore realtime listeners (messages · proposals ·
 * ledger). Sending a message writes the VERBATIM record, then runs the
 * extraction SEAM (deterministic mock — see lib/extractor.ts) and persists
 * derivative proposals FK'd to the message. Nothing writes to the project
 * record until a human commits; commit appends an immutable ledger entry,
 * reject writes nothing.
 *
 * Owner-locked behaviours honoured here:
 *  - verbatim sacrosanct (append-only; messages never edited)
 *  - AI = ambient chip by default; bordered bubble only when AI is asking
 *  - single Review Queue; commit-or-reject gate
 *  - merged Conversation surface (text + 📎 + 🎙 one input row)
 *  - mobile Review Queue: swipe right = commit · swipe left = reject
 *  - pure-white surfaces, semantic colour (commit = green primary)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { visibilityFor } from '../lib/roles';
import { useToast } from '../components/Toast';
import { Btn3, StageRail } from '../components/ui';
import { demoSeedEnabled } from '../lib/demoSeed';
import { activeExtractor } from '../lib/extractor';
import {
  DEMO_PID,
  commitProposal,
  ensureDemoProject,
  getProjectMeta,
  listenLedger,
  listenMessages,
  listenProposals,
  listenRecommendations,
  recordRecommendation,
  postMessage,
  postProposalsFromDrafts,
  queueColour,
  rejectProposal,
  visibleThread,
  messageKindOf,
  type ConvMessage,
  type LedgerEntry,
  type ProjectMeta,
  type Proposal,
} from '../lib/conversation';
import {
  activeTopicMatcher,
  recallPriorRecommendations,
  resolveTemplateVersion,
  type RecommendationEvent,
} from '../lib/projectLedger';
import {
  ALL_TIERS,
  getEngagementTemplate,
  isSurfaceUnlocked,
  stageRailFor,
  tabsFor,
  type Tier,
} from '../lib/engagementTemplate';
import { businessHoursUntil } from '../lib/workingCalendar';
import { useClientNames } from '../lib/useClientNames';
import { projectLabel } from '../lib/projectStore';
import {
  getReportDownloadUrl,
  downloadTierFromAttachment,
} from '../lib/reportDelivery';
import type { Attachment } from '../lib/conversation';

/* ---------- helpers ---------- */

function dayKey(ms: number): string {
  const d = new Date(ms);
  const today = new Date();
  const y = new Date(today);
  y.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const date = d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  if (same(d, today)) return `Today · ${date}`;
  if (same(d, y)) return `Yesterday · ${date}`;
  return date;
}

function hhmm(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function escalationHrs(p: Proposal): number | null {
  if (!p.escalateAt) return null;
  // Sprint 3: the anchor is working-calendar resolved, so the countdown
  // is *business* hours remaining (skips weekends/holidays/after-hours),
  // not raw wall-clock delta.
  return businessHoursUntil(new Date(p.escalateAt.toMillis()));
}

type Filter = 'all' | 'create_finding' | 'update_intake' | 'photo_tag';

/* ════════════════════ Screen ════════════════════ */

export default function Conversation() {
  const { user, role } = useAuth();
  const { toast } = useToast();

  // THE SPINE — owner-locked visibility split (2026-05-17). Team + Admin
  // see the full thread + the AI/Review layer + internal notes. Client
  // (and every non-team role) gets the spine-filtered view: their own
  // messages + team replies + the 3 client-visible milestones, and NO
  // Review Queue / AI extraction / internal notes anywhere. Resolved once,
  // here, from the locked role model.
  const vis = visibilityFor(role);
  const isTeamView = vis.seesReviewLayer; // admin + staff only

  const [meta, setMeta] = useState<ProjectMeta | null>(null);
  const [messages, setMessages] = useState<ConvMessage[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [recs, setRecs] = useState<RecommendationEvent[]>([]);
  const [text, setText] = useState('');
  // AD-005 — default team messages to internal; a "Send to client →" button
  // flips toClient=true for that send only. `toClient` is the primary state;
  // the data-layer `internal` flag is derived from it at send time.
  const [toClient, setToClient] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [jumpId, setJumpId] = useState<string | null>(null);
  // DEMO/PREVIEW ONLY — lets a reviewer view any tier's stage rail +
  // tabs + gates without changing real project tier persistence. null =
  // use the project's actual tier.
  const [previewTier, setPreviewTier] = useState<Tier | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [exiting, setExiting] = useState<Record<string, 'commit' | 'reject'>>({});
  const [recording, setRecording] = useState(false);

  const streamRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  // DEMO_PID is DEAD as a hardwired constant. The project id now comes
  // from the route (`/conversation/:pid` — a real converted project's own
  // thread). The bare `/conversation` (no pid) keeps landing on the demo
  // fixture ONLY as the reviewer's seeded walkthrough — every real lead
  // conversion routes to its own pid, so staffers never collide.
  const { pid: routePid } = useParams();
  const pid = routePid ?? DEMO_PID;
  const isDemo = pid === DEMO_PID;
  // #992: bare /conversation (no project in the route) is only the VP656
  // reviewer fixture when the demo seed is explicitly on. With it OFF
  // (default / production) bare /conversation is a neutral empty state —
  // never the fabricated VP656 thread. A :pid route always loads its real
  // project regardless of the flag.
  const noProject = !routePid && !demoSeedEnabled();

  const actor = {
    uid: user?.uid ?? 'demo',
    name: user?.displayName || user?.email?.split('@')[0] || 'You',
  };

  /* ---- bootstrap project (real or demo) + realtime listeners ---- */
  useEffect(() => {
    let alive = true;
    // Real project: read its persisted meta. Demo fixture: idempotently
    // ensure the seeded demo doc (reviewer walkthrough only).
    if (isDemo) {
      ensureDemoProject().then((m) => alive && setMeta(m)).catch(() => {});
    } else {
      getProjectMeta(pid).then((m) => alive && setMeta(m)).catch(() => {});
    }
    const u1 = listenMessages(pid, setMessages, () =>
      toast('Could not load the conversation (check connection)'),
    );
    const u2 = listenProposals(pid, setProposals, () =>
      toast('Review Queue could not load — this is an error, not an empty queue'),
    );
    const u3 = listenLedger(pid, setLedger, () =>
      toast('Project Ledger could not load (check connection)'),
    );
    // Sprint-4 auto-recall source: recommendation events on this project.
    const u4 = listenRecommendations(pid, setRecs, () =>
      toast('Prior recommendations could not load (check connection)'),
    );
    return () => {
      alive = false;
      u1();
      u2();
      u3();
      u4();
    };
  }, [pid, toast]);

  /* ---- autoscroll on new message ---- */
  useEffect(() => {
    const el = streamRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, proposals.length]);

  /* ---- on open, put the cursor in the verbatim message box so the
     owner can start typing immediately (keystrokes land here) ---- */
  useEffect(() => {
    const id = window.requestAnimationFrame(() => taRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, []);

  /* ---- compose / send (verbatim → extract → propose) ---- */
  async function send() {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      // AD-005: default team messages are internal; only `toClient` sends
      // flip the message to client-visible. Client-view messages are always
      // client-visible (isTeamView=false → internal=false).
      const isInternal = isTeamView ? !toClient : false;
      const msgId = await postMessage(pid, {
        authorName: actor.name,
        authorUid: actor.uid,
        authorRole: isTeamView ? 'staff' : 'client',
        body,
        internal: isInternal,
      });
      setText('');
      // AD-005: reset toClient to false after every send so the composer
      // reverts to the internal default.
      setToClient(false);
      if (taRef.current) taRef.current.style.height = 'auto';
      // EXTRACTION SEAM — deterministic mock for Sprint 2.
      const drafts = activeExtractor.extract({
        id: msgId,
        authorName: actor.name,
        authorUid: actor.uid,
        body,
        attachments: [],
        voice: null,
        createdAt: null,
        localTs: Date.now(),
      });
      await postProposalsFromDrafts(pid, drafts, tpl);
    } catch {
      toast('Message could not be sent');
    } finally {
      setBusy(false);
    }
  }

  /* ---- commit / reject (with swipe-out animation) ---- */
  function resolve(p: Proposal, action: 'commit' | 'reject') {
    setExiting((x) => ({ ...x, [p.id]: action }));
    window.setTimeout(async () => {
      try {
        if (action === 'commit') {
          await commitProposal(pid, p, actor);
          // Sprint-4: committing a finding records a RECOMMENDATION event
          // (auto-recall source). The topicKey is derived deterministically
          // from the finding so a later re-ask about the same concern
          // surfaces this — memory feedback_avbp_recommendation_memory.
          if (p.kind === 'create_finding') {
            const concern = `${p.title} ${p.detail}`;
            await recordRecommendation(
              pid,
              activeTopicMatcher.keyFor(concern),
              `${p.title}: ${p.severity ?? 'reviewed'} — ${p.detail}`,
              actor,
              p.sourceMessageId,
            );
          }
          toast('Committed to ' + pid + ' record · logged to Project Ledger', {
            ok: true,
          });
        } else {
          await rejectProposal(pid, p.id);
          toast('Proposal rejected · nothing written');
        }
      } catch {
        toast('Action failed — nothing was written');
        setExiting((x) => {
          const n = { ...x };
          delete n[p.id];
          return n;
        });
      }
    }, 460);
  }

  function jumpToSource(p: Proposal) {
    setJumpId(p.sourceMessageId);
    setSheetOpen(false);
    const el = document.getElementById('m-' + p.sourceMessageId);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    toast('Jumped to source message');
    window.setTimeout(() => setJumpId(null), 1800);
  }

  /* ---- open a delivered-report PDF chip (Phase-2 Slice 3) ----
     A delivered report chip carries the report FKs (tier + runId + storagePath).
     clientId + projectId come from the conversation context. We mint a SHORT-
     lived signed URL via the getReportDownloadUrl CF (the server is the SOLE
     entitlement authority — team OR own client, delivered only, FK-checked) and
     open it. A blocked request surfaces the server message verbatim. */
  const [openingChip, setOpeningChip] = useState<string | null>(null);
  function isReportChip(a: Attachment): boolean {
    return a.kind === 'pdf' && !!(a.tier || a.storagePath);
  }
  async function openReportAttachment(a: Attachment, key: string) {
    if (openingChip) return;
    const tier = downloadTierFromAttachment(a.tier);
    const cid = meta?.clientId;
    if (!tier || !cid || !a.runId) {
      toast('This report is not available to open yet.');
      return;
    }
    setOpeningChip(key);
    try {
      const res = await getReportDownloadUrl({
        clientId: cid,
        projectId: pid,
        tier,
        runId: a.runId,
      });
      window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      toast((e as Error).message || 'Could not open the report.');
    } finally {
      setOpeningChip(null);
    }
  }

  /* ---- engagement template (per-tier source of truth) ----
     Resolves the stage rail + workflow tab set from the project's tier.
     Sprint-2 left these hardcoded for the L2B demo + the tabs visibly
     dimmed; Sprint 3 makes them template-driven. Falls back to L2B (the
     Sprint-2 reference tier) for an unknown/missing tier and says so. */
  // Preview override (demo-only) wins; otherwise the project's tier;
  // otherwise the L2B Sprint-2 reference fallback.
  const tpl = useMemo(
    () =>
      (previewTier ? getEngagementTemplate(previewTier) : null) ??
      getEngagementTemplate(meta?.tier) ??
      getEngagementTemplate('L2B')!,
    [previewTier, meta?.tier],
  );
  const tierResolved = previewTier != null || getEngagementTemplate(meta?.tier) != null;

  // Canonical client name — `meta.clientName` is a display cache that can
  // drift; the clients/{id} master is the single source of truth. Resolve
  // it so the conversation header shows the same name as every other
  // screen (AVBP_DATA_MODEL_IA_SPEC_v1).
  const { resolveName } = useClientNames();
  const clientName = meta
    ? resolveName(meta.clientId || meta.clientNo, meta.clientName)
    : null;

  /* ---- Sprint-4: template version LOCK resolution ----
     The project locked its engagementTemplate version at creation;
     streamline edits apply only to NEW engagements. Resolve the version
     this shell renders by; legacy/demo projects with no locked version
     fall back to current and the UI says so honestly. */
  const tplVersion = useMemo(
    () => resolveTemplateVersion(meta?.templateVersion),
    [meta?.templateVersion],
  );

  /* ---- Sprint-4: auto-recall ----
     As the owner types a concern, surface prior recommendations recorded
     on THIS project for the same topic (deterministic topicKey match via
     the TopicMatcher seam). Long engagements (L2C 24mo, L3B) stay
     consistent — memory feedback_avbp_recommendation_memory. */
  const recall = useMemo(
    () => recallPriorRecommendations(recs, text),
    [recs, text],
  );
  // Sprint-3 demo: the conversation fixture sits at the first
  // post-engagement stage (real per-project stage state lands with the
  // Ledger sprint). Index 1 keeps "engaged → now" intuitive.
  const activeStageKey = useMemo(
    () => tpl.stages[Math.min(1, tpl.stages.length - 1)].key,
    [tpl],
  );
  const railStages = useMemo(
    () => stageRailFor(tpl, activeStageKey),
    [tpl, activeStageKey],
  );
  const workflowTabs = useMemo(() => tabsFor(tpl), [tpl]);

  /* ---- derived ---- */
  const openProps = useMemo(
    () => proposals.filter((p) => p.status === 'open' && !p.asksUser),
    [proposals],
  );
  const filtered = useMemo(
    () => (filter === 'all' ? openProps : openProps.filter((p) => p.kind === filter)),
    [openProps, filter],
  );
  const qc = queueColour(openProps.length);
  const qLabel = qc === 'r' ? 'heavy' : qc === 'a' ? 'building' : 'clear';

  /* THE SPINE: the SAME thread, role-filtered once. Team/admin see every
     message + the AI beats; client (+ any non-team role) sees only the
     spine-visible subset (own + team chat + 3 client milestones) and NO AI
     beats at all (the review layer is structurally absent for them). */
  const threadMsgs = useMemo(
    () => visibleThread(messages, { isTeamView }),
    [messages, isTeamView],
  );

  /* messages + their AI beats, grouped by day */
  const stream = useMemo(() => {
    const rows: Array<
      | { t: 'day'; key: string }
      | { t: 'msg'; m: ConvMessage }
      | { t: 'ai'; m: ConvMessage; props: Proposal[] }
    > = [];
    let lastDay = '';
    for (const m of threadMsgs) {
      const dk = dayKey(m.localTs);
      if (dk !== lastDay) {
        rows.push({ t: 'day', key: dk });
        lastDay = dk;
      }
      rows.push({ t: 'msg', m });
      // AI beats are TEAM-ONLY. Never rendered on the client track.
      if (isTeamView) {
        const ps = proposals.filter((p) => p.sourceMessageId === m.id);
        if (ps.length) rows.push({ t: 'ai', m, props: ps });
      }
    }
    return rows;
  }, [threadMsgs, proposals, isTeamView]);

  /* ---- review-queue list (shared desktop aside + mobile sheet) ---- */
  function QueueList() {
    return (
      <>
        <div className="rqhead">
          <div className="top">
            <h2 className="serif">Review Queue</h2>
            <span
              className="statuspill"
              title="Queue depth — green <4 · amber 4–6 · red >6"
            >
              <span className={`pip ${qc}`} />
              {openProps.length} open · {qLabel}
            </span>
          </div>
          <div className="rqsub">
            Proposals extracted from the conversation — you commit or reject.
            Nothing writes to the record until you do.
          </div>
          <div className="filt">
            {(
              [
                ['all', 'All'],
                ['create_finding', 'Findings'],
                ['update_intake', 'Intake'],
                ['photo_tag', 'Tags'],
              ] as Array<[Filter, string]>
            ).map(([k, label]) => (
              <button
                key={k}
                className={`pill ${filter === k ? 'on' : ''}`}
                onClick={() => setFilter(k)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="rqlist">
          {filtered.length === 0 && (
            <div className="rq-empty">
              <span className="serif">Queue clear</span>
              Send a message in the conversation — the AI extracts proposals
              here. Try “MD cabin 12×14, toilet in SE — that's a problem.”
            </div>
          )}
          {filtered.map((p) => {
            const ex = exiting[p.id];
            const hrs = escalationHrs(p);
            return (
              <QueueCard
                key={p.id}
                p={p}
                exiting={ex}
                hrs={hrs}
                onCommit={() => resolve(p, 'commit')}
                onReject={() => resolve(p, 'reject')}
                onSource={() => jumpToSource(p)}
              />
            );
          })}
        </div>
      </>
    );
  }

  /* Sprint-2/4 verification footer — proves the immutable Project Ledger
     is writing. Lives INSIDE the Review Queue panel (never overlays the
     composer). Temporary review aid; the full Ledger UI is a later sprint. */
  function LedgerFoot() {
    if (ledger.length === 0) return null;
    return (
      <div
        className="ledger-foot"
        title="Project Ledger proof — immutable per-project record (all event types). Temporary verification strip; the full Ledger screen is a later sprint."
      >
        <b>Project Ledger</b> · {ledger.length} events ·{' '}
        {ledger[0]?.eventType ?? ledger[0]?.kind ?? 'event'} ↩{' '}
        {ledger[0]?.actorName} · tpl {tplVersion.version}
        {recall.length > 0 && ` · ${recall.length} recall`}
      </div>
    );
  }

  if (noProject) {
    return (
      <div className="conv-screen">
        <section className="conv-main">
          <header className="conv-phead">
            <b>Conversation</b>
          </header>
          <div style={{ padding: 32, color: 'var(--ink-soft)', lineHeight: 1.7 }}>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--ink)' }}>
              No project selected.
            </p>
            <p style={{ marginTop: 8 }}>
              Open a project from <b>Clients &amp; Projects</b> to see its
              conversation thread. Each project has its own private thread.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`conv-screen${isTeamView ? '' : ' client-view'}`}>
      {/* ───────── Conversation column ───────── */}
      <section className="conv-main">
        <header className="conv-phead">
          <div className="crumb">
            {meta?.vp ?? '…'} · {clientName ?? '…'} ·{' '}
            {meta?.clientNo ?? ''}
          </div>
          <div className="ptitle">
            {/* The project's own name leads the conversation header; the VP
                code + client identity sit in the crumb above. A nameless
                project falls back to a `VP### · tier` placeholder. */}
            <h1 className="serif">
              {meta
                ? projectLabel(meta.projectName, meta.vp, meta.tier)
                : 'Loading…'}
            </h1>
            {meta && (
              <span
                className="ptag"
                title={
                  previewTier
                    ? `Previewing ${tpl.tier} · ${tpl.displayName} — demo override, not the real project tier (${meta.tier})`
                    : tierResolved
                      ? `${tpl.tier} · ${tpl.displayName} — stage rail + tabs resolved from engagementTemplate · template ${
                          tplVersion.exact
                            ? `LOCKED to ${tplVersion.version} at project creation (streamline edits apply only to new engagements)`
                            : `version ${tplVersion.version} (current — project's locked version unavailable, legacy/demo)`
                        }`
                      : `Unknown tier "${meta.tier}" — falling back to L2B reference template`
                }
              >
                {previewTier ? `${tpl.tier} (preview)` : meta.tier}
              </span>
            )}
          </div>

          {/* DEV/DEMO ONLY — tier preview switcher. Internal review tool,
              TEAM-ONLY (never on the client track). Lets a reviewer swap
              the active tier so the stage rail, tabs, gates + follow-up
              re-resolve from engagementTemplate. Does NOT change real
              project tier. */}
          {isTeamView && (
          <div
            className="tier-preview"
            role="group"
            aria-label="Preview tier (demo tool)"
            title="Demo tool — preview any tier's stage rail, tabs and gates. Does not change the real project tier."
          >
            <span className="tp-lbl">⚙ Preview tier</span>
            {ALL_TIERS.map((tr) => (
              <button
                key={tr}
                className={`tp-btn${
                  tpl.tier === tr && (previewTier === tr) ? ' on' : ''
                }`}
                aria-pressed={previewTier === tr}
                onClick={() => setPreviewTier(tr)}
              >
                {tr}
              </button>
            ))}
            <button
              className={`tp-btn tp-reset${previewTier === null ? ' on' : ''}`}
              onClick={() => setPreviewTier(null)}
              title={`Use the project's real tier (${meta?.tier ?? '…'})`}
            >
              Reset
            </button>
          </div>
          )}
          <StageRail stages={railStages} />
          <div className="conv-tabs">
            <button className="conv-tab on">Messages</button>
            {workflowTabs
              .filter((tb) => tb.key !== 'messages')
              .map((tb) => {
                const surface =
                  tb.key === 'marma'
                    ? 'marma'
                    : tb.key === 'devta'
                      ? 'devta'
                      : null;
                const locked =
                  surface != null &&
                  !isSurfaceUnlocked(tpl, surface, activeStageKey);
                const title = locked
                  ? `${tb.label} — unlocks at the ${
                      surface === 'marma'
                        ? 'structural / MAP / HVAC drawing'
                        : 'consolidated remedy-list'
                    } stage (part of ${tpl.tier})`
                  : tb.laterSprint
                    ? `${tb.label} — surface lands in a later sprint (part of ${tpl.tier})`
                    : tb.label;
                return (
                  <button
                    key={tb.key}
                    className="conv-tab"
                    disabled={tb.laterSprint || locked}
                    title={title}
                  >
                    {tb.label}
                    {locked ? ' 🔒' : ''}
                  </button>
                );
              })}
          </div>
        </header>

        <div className="conv-stream" ref={streamRef}>
          {stream.length === 0 && (
            <div className="rq-empty" style={{ marginTop: 40 }}>
              <span className="serif">No messages yet</span>
              This is the verbatim record for {pid}. Send a message below —
              text, and (in a later sprint) photo · voice · file. The AI will
              extract proposals into the Review Queue.
            </div>
          )}
          {stream.map((row, i) => {
            if (row.t === 'day')
              return (
                <div className="daysep" key={`d${i}`}>
                  {row.key}
                </div>
              );
            if (row.t === 'msg') {
              const m = row.m;
              // THE SPINE: a milestone event renders as a centered system
              // entry IN the same thread (quote generated · report sent ·
              // payment made are client-visible; gate/stage are team-only
              // and already filtered out for clients upstream).
              if (messageKindOf(m) === 'milestone') {
                // Phase-2 Slice 2: a report_sent / deliverable milestone carries
                // its PDF attachment — render the ▤ chip BELOW the milestone
                // chip (the old code returned before the attachments block, so a
                // delivered report never showed its PDF). Other milestones (quote
                // generated · payment made) have no attachments → chip only.
                // (Open/download via signed URL is Slice 3.)
                return (
                  <div className="milestone-row" key={m.id} id={'m-' + m.id}>
                    <span className="milestone-chip">
                      <span className="dot" aria-hidden />
                      {m.body}
                    </span>
                    {m.attachments.length > 0 && (
                      <div className="att milestone-att">
                        {m.attachments.map((a, k) => (
                          <AttachmentChip
                            key={k}
                            a={a}
                            chipKey={`${m.id}-${k}`}
                            openable={isReportChip(a)}
                            busy={openingChip === `${m.id}-${k}`}
                            onOpen={() => openReportAttachment(a, `${m.id}-${k}`)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              // BUILD B (item 5): a per-message client-visibility signal.
              // `internal` → a team-only note (never reaches a client);
              // otherwise the message is shared with the client. The
              // signal is shown on the team view only — on the client
              // track every visible message is client-visible by
              // definition (internal ones are already filtered out).
              const isInternal = m.internal === true;
              return (
                <div
                  key={m.id}
                  id={'m-' + m.id}
                  className={`msg${jumpId === m.id ? ' jump' : ''}${
                    isInternal ? ' internal' : ''
                  }`}
                >
                  <div className="who">
                    {m.authorName} <span className="t">{hhmm(m.localTs)}</span>
                    {isTeamView && (
                      <span
                        className={`msg-vis conv-tag-cli ${isInternal ? 'int' : 'shared'}`}
                        title={
                          isInternal
                            ? 'Internal note — team only, never visible to the client'
                            : 'Client-visible — the client sees this message'
                        }
                      >
                        {isInternal ? 'internal' : 'sent to client'}
                      </span>
                    )}
                  </div>
                  <div className="bub">
                    {m.body}
                    {m.attachments.length > 0 && (
                      <div className="att">
                        {m.attachments.map((a, k) => (
                          <AttachmentChip
                            key={k}
                            a={a}
                            chipKey={`${m.id}-${k}`}
                            openable={isReportChip(a)}
                            busy={openingChip === `${m.id}-${k}`}
                            onOpen={() => openReportAttachment(a, `${m.id}-${k}`)}
                          />
                        ))}
                      </div>
                    )}
                    {m.voice && (
                      <>
                        <div className="voice">
                          <button className="play" aria-label="Play voice note">
                            ▶
                          </button>
                          <span className="wave">
                            {[6, 13, 9, 16, 7, 11, 5, 14].map((h, k) => (
                              <i key={k} style={{ height: h }} />
                            ))}
                          </span>
                          {Math.round(m.voice.durationSec)}s
                        </div>
                        <div className="vtx">
                          “{m.voice.transcript}” · transcribed
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            }
            // AI beat
            const asks = row.props.filter((p) => p.asksUser && p.status === 'open');
            const ambient = row.props.filter(
              (p) => !p.asksUser && p.status === 'open',
            );
            return (
              <div className="aiwrap" key={`ai${row.m.id}`}>
                {asks.map((p) => (
                  <div className="aibub" key={p.id}>
                    <div className="h">
                      <span className="k" aria-hidden>
                        AI
                      </span>
                      Needs your call
                    </div>
                    <p>{p.detail}</p>
                    <div className="acts">
                      <button
                        className="mini pri"
                        onClick={() => resolve(p, 'commit')}
                      >
                        Add as Client Circle
                      </button>
                      <button className="mini" onClick={() => resolve(p, 'reject')}>
                        Not now
                      </button>
                    </div>
                  </div>
                ))}
                {ambient.length > 0 && (
                  <span className="chip">
                    <span className="k" aria-hidden>
                      AI
                    </span>
                    <span>
                      <b>
                        {ambient.length} proposal{ambient.length > 1 ? 's' : ''}
                      </b>{' '}
                      from this message
                    </span>
                    <button
                      className="lnk"
                      onClick={() => {
                        const mobile = window.matchMedia(
                          '(max-width: 1100px)',
                        ).matches;
                        if (mobile) {
                          setSheetOpen(true);
                          toast('Open in the Review Queue →');
                          return;
                        }
                        const el = document.getElementById('rq-aside');
                        el?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        });
                        el?.classList.add('rq-flash');
                        window.setTimeout(
                          () => el?.classList.remove('rq-flash'),
                          1200,
                        );
                        toast('See the Review Queue →');
                      }}
                    >
                      Review ↗
                    </button>
                  </span>
                )}
                {asks.length === 0 && ambient.length === 0 && (
                  <span className="chip good">
                    <span className="k" aria-hidden>
                      ✓
                    </span>
                    <span>All proposals from this message resolved</span>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Sprint-4 AUTO-RECALL — prior recommendations for the concern
            being raised. Surfaces only when the typed concern matches a
            recorded topic on THIS project (memory
            feedback_avbp_recommendation_memory). Keeps long engagements
            consistent — staff sees what was already recommended. */}
        {recall.length > 0 && (
          <div
            className="recall"
            role="note"
            aria-label="Previously recommended for this project"
            style={{
              margin: '0 0 8px',
              padding: '8px 12px',
              border: '1px solid var(--rule)',
              borderLeft: '3px solid var(--ai, #6d28d9)',
              borderRadius: 'var(--r-sm)',
              background: 'var(--surface)',
              fontSize: 12.5,
              color: 'var(--ink-mute)',
            }}
          >
            <b style={{ color: 'var(--ink)' }}>
              Previously recommended for this project
            </b>{' '}
            · {recall[0].topicKey}
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {recall.map((r) => (
                <li key={r.id} style={{ marginBottom: 2 }}>
                  {r.recommendation}{' '}
                  <span style={{ opacity: 0.7 }}>— {r.actorName}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* merged composer: text + 📎 + 🎙 in one row */}
        <div className={`composer${!toClient && isTeamView ? ' is-internal' : ''}`}>
          <div className="cbox">
            <button
              className="ibtn"
              title="Attach photo · video · audio · PDF · DWG (wiring lands with Storage sprint)"
              onClick={() =>
                toast('Attachment upload lands with the Storage sprint')
              }
            >
              📎
            </button>
            <textarea
              ref={taRef}
              className="ti"
              rows={1}
              placeholder={
                isTeamView && !toClient
                  ? 'Internal note — team only, the client will not see this…'
                  : 'Message — text now; photo · voice · file land with the Storage sprint…'
              }
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                const t = e.target;
                t.style.height = 'auto';
                t.style.height = Math.min(t.scrollHeight, 120) + 'px';
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <button
              className={`ibtn ${recording ? 'rec' : ''}`}
              title="Hold to speak (transcription seam — Whisper, later sprint)"
              onClick={() => {
                setRecording((r) => !r);
                toast('Voice capture lands with the Whisper transcription sprint');
              }}
            >
              🎙
            </button>
            <button
              className="ibtn send"
              title="Send"
              disabled={!text.trim() || busy}
              onClick={send}
            >
              ↑
            </button>
          </div>
          <div className="chint">
            {/* AD-005 — "Send to client →" button replaces the opt-in tick-box.
                Default is internal (team-only note); one explicit tap flips the
                NEXT send to client-visible and reverts after. */}
            {isTeamView ? (
              <button
                type="button"
                className={`cs-tag-cli${toClient ? ' active' : ''}`}
                style={{
                  background: toClient ? 'var(--good)' : 'var(--inset)',
                  border: '1px solid var(--rule)',
                  borderRadius: 'var(--r-sm)',
                  padding: '3px 10px',
                  fontSize: 12,
                  cursor: 'pointer',
                  color: toClient ? '#fff' : 'var(--ink-mute)',
                }}
                onClick={() => {
                  setToClient((t) => !t);
                }}
              >
                {toClient ? '✓ Send to client' : 'Send to client →'}
              </button>
            ) : (
              <span>
                Verbatim message + attachments are the canonical record · AI
                extraction is derivative
              </span>
            )}
            <span>Enter to send · Shift+Enter newline</span>
          </div>
        </div>
      </section>

      {/* ───────── Review Queue : desktop aside (TEAM-ONLY) ─────────
          THE SPINE: the AI/Review layer is layered on the SAME thread for
          team + admin and is structurally NEVER present on the client
          track. The client simply does not get this column. */}
      {isTeamView && (
        <aside className="rq aside" id="rq-aside">
          <QueueList />
          <LedgerFoot />
        </aside>
      )}

      {/* ───────── Review Queue : mobile bottom sheet (TEAM-ONLY) ───────── */}
      {isTeamView && (
      <button
        className="rq-fab"
        onClick={() => setSheetOpen(true)}
        aria-label={`Open Review Queue — ${openProps.length} open`}
      >
        <span className={`pip ${qc}`} />
        Review Queue · {openProps.length}
      </button>
      )}
      {isTeamView && sheetOpen && (
        <>
          <div className="sheet-scrim" onClick={() => setSheetOpen(false)} />
          <div
            className="rq sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Review Queue"
          >
            <div className="sheet-grip" />
            <div className="sheet-hint">
              Swipe a card right to commit · left to reject
            </div>
            <QueueList />
            <LedgerFoot />
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- one Review-Queue card (desktop click + mobile swipe) ---------- */

function QueueCard({
  p,
  exiting,
  hrs,
  onCommit,
  onReject,
  onSource,
}: {
  p: Proposal;
  exiting?: 'commit' | 'reject';
  hrs: number | null;
  onCommit: () => void;
  onReject: () => void;
  onSource: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const start = useRef<number | null>(null);
  const [dx, setDx] = useState(0);

  function onTouchStart(e: React.TouchEvent) {
    start.current = e.touches[0].clientX;
  }
  function onTouchMove(e: React.TouchEvent) {
    if (start.current == null) return;
    setDx(e.touches[0].clientX - start.current);
  }
  function onTouchEnd() {
    const TH = 72;
    if (dx > TH) onCommit();
    else if (dx < -TH) onReject();
    setDx(0);
    start.current = null;
  }
  function onLongPress() {
    // long-press = admin reassign (handler seam — reassign flow is a
    // later sprint; the gesture is wired so mobile parity is real today)
    onSource();
  }

  const kindClass =
    p.kind === 'create_finding'
      ? 'find'
      : p.kind === 'photo_tag'
        ? 'tag'
        : '';

  return (
    <div
      ref={ref}
      className={`card rqcard${
        exiting === 'commit'
          ? ' go-commit'
          : exiting === 'reject'
            ? ' go-reject'
            : ''
      }${dx !== 0 ? ' drag' : ''}`}
      style={dx !== 0 ? { transform: `translateX(${dx}px)` } : undefined}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onContextMenu={(e) => {
        e.preventDefault();
        onLongPress();
      }}
    >
      <div className="k">
        <span className={`ktag ${kindClass}`}>{p.kind}</span>
        {p.severity && <span className="sev">{p.severity}</span>}
      </div>
      <div className="body">
        <b>{p.title}</b> — {p.detail}
      </div>
      <button className="src" onClick={onSource}>
        {p.sourceLabel}
      </button>
      {hrs != null && (
        <div className="esc">
          ⏱ Auto-escalates to admin in {hrs}h (24-business-hr rule)
        </div>
      )}
      <div className="cact">
        <Btn3 intent="grn" size="sm" onClick={onCommit}>
          Commit
        </Btn3>
        <Btn3 intent="ghost" size="sm" onClick={onReject}>
          Reject
        </Btn3>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => onSource()}
          title="Edit before commit — lands with the engagementTemplate sprint"
        >
          Edit
        </Btn3>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────
 *  AttachmentChip — a single attachment thumb. A delivered-report PDF
 *  chip (openable) becomes a button that mints a signed URL on click
 *  (Slice 3); every other attachment renders the inert thumb as before.
 * ────────────────────────────────────────────────────────────────── */
function AttachmentChip({
  a,
  chipKey,
  openable,
  busy,
  onOpen,
}: {
  a: Attachment;
  chipKey: string;
  openable: boolean;
  busy: boolean;
  onOpen: () => void;
}) {
  const glyph =
    a.kind === 'photo'
      ? '▦'
      : a.kind === 'pdf'
        ? '▤'
        : a.kind === 'dwg'
          ? '◈'
          : '▣';
  if (openable) {
    return (
      <button
        type="button"
        className="thumb thumb-open"
        title={busy ? 'Opening…' : `Open ${a.name ?? 'report'}`}
        onClick={onOpen}
        disabled={busy}
        data-chip={chipKey}
        aria-label={`Open ${a.name ?? 'delivered report'}`}
      >
        {busy ? '…' : glyph}
      </button>
    );
  }
  return (
    <div className="thumb" title={a.name ?? a.kind}>
      {glyph}
    </div>
  );
}
